import { Component, Input } from '@angular/core';
import { RouterPath } from '../../shared/models';
import { LocaleService } from '../../core/services/locale.service';

/**
 * Breadcrumb item interface
 */
export interface BreadcrumbItem {
    /** Display label for the breadcrumb */
    label: string;
    /** Router link path (array for routerLink) - if provided, item is clickable */
    path?: string | string[] | null;
    /** Click handler function - if provided, item is clickable via click handler */
    onClick?: () => void;
    /** Whether this is the current/last item (typically not clickable) */
    isActive?: boolean;
}

/**
 * Reusable breadcrumb component that works with language-prefixed routing.
 * The home label is automatically translated using the i18n key 'breadcrumb.home'.
 *
 * @example
 * ```html
 * <app-breadcrumb class="breadcrumb" [items]="breadcrumbItems"></app-breadcrumb>
 * ```
 *
 * @example
 * ```typescript
 * breadcrumbItems: BreadcrumbItem[] = [
 *   { label: 'breadcrumb.home', path: [RouterPath.HOME] },
 *   { label: 'Products', path: ['get-data', 'data-domains', 'dataset-groups'] },
 *   { label: 'Dataset Group', path: ['get-data', 'data-domains', 'dataset-groups', '2'] },
 *   { label: 'Dataset Name', isActive: true }
 * ];
 * ```
 */
@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent {
    /**
     * Breadcrumb items to display.
     * If empty, only shows home link.
     */
    @Input() items: BreadcrumbItem[] = [];

    /**
     * Whether to show the home link.
     * Default: true
     * Note: Home label uses i18n key 'breadcrumb.home'
     */
    @Input() showHome: boolean = true;

    /**
     * Separator between breadcrumb items.
     * Default: '/'
     */
    @Input() separator: string = '/';

    /**
     * Theme for breadcrumb styling.
     * 'black' - for light backgrounds (dark text)
     * 'white' - for dark backgrounds (light text)
     * Default: 'black'
     */
    @Input() theme: 'black' | 'white' = 'black';

    protected readonly RouterPath = RouterPath;

    constructor(private localeService: LocaleService) {}

    /**
     * Handle breadcrumb item click
     */
    onItemClick(item: BreadcrumbItem, event?: Event): void {
        if (event) {
            event.preventDefault();
        }

        if (item.onClick) {
            item.onClick();
        }
    }

    /**
     * Check if item is clickable
     */
    isClickable(item: BreadcrumbItem): boolean {
        // Item is clickable if it's not active and has either a path or onClick handler
        return !item.isActive && (item.path !== null && item.path !== undefined || !!item.onClick);
    }

    goToHome(event?: Event): void {
        if (event) {
            event.preventDefault();
        }
        this.localeService.navigate(RouterPath.HOME);
    }

    goToPath(path: string | string[]): void {
        this.localeService.navigate(path);
    }
}
